const STAGING_STACK_NAME_PATTERN = /^[a-z0-9][a-z0-9-]{0,11}[a-z0-9]$/;

export const SHARED_STAGING_STACK_NAME = "staging";
export const STAGING_AIRLOCK_BASE_URL = "https://airlock.staging-api.replo.app";
export const SHARED_STAGING_GIT_PROXY_PUBLIC_ORIGIN =
  "https://staging-git.replo.app";
export const LOCAL_GIT_PROXY_PUBLIC_ORIGIN = "http://localhost:8790";

// Production backend origins. Single source of truth for both the editor's URL
// resolvers (apps/replo-agent/src/v2/utils/env.ts) and the miniapp bedrock
// client (packages/bedrock-sdk/miniapp.ts), so the REPLO_LOCAL_PROD_DATA
// "local frontend -> prod backend" flow points every surface at the same host.
export const PRODUCTION_BEDROCK_INTERNAL_API_BASE_URL =
  "https://bedrock.api.replo.app";
export const PRODUCTION_AGENT_COORDINATOR_BASE_URL =
  "https://cloudflare-agent-coordinator-production.replo.workers.dev";
export const PRODUCTION_AIRLOCK_BASE_URL = "https://airlock.replo.app";
export const PRODUCTION_GIT_PROXY_PUBLIC_ORIGIN = "https://git.replo.app";
export const PRODUCTION_PUBLISHER_BASE_URL = "https://publisher.replo.app";

/**
 * Names that pass STAGING_STACK_NAME_PATTERN but belong to long-lived workers
 * rather than a stack (`bedrock-api-ci` is the CI worker), so
 * `bedrock-api-<name>` resolves to something that is not a stack's deployment.
 * `apps/eval-supervisor/container/stack-target-contract.mts` keeps a
 * dependency-free mirror of this set for the same reason it mirrors the
 * pattern: its container carries no node_modules.
 */
export const NON_STACK_WORKER_NAMES: ReadonlySet<string> = new Set([
  "ci",
  "test",
  "workers-test",
]);

class StagingStackError extends Error {}

export function getStagingStackName({
  inputName,
  rawName,
}: {
  inputName: string;
  rawName: string | undefined;
}): string {
  const stackName = rawName?.trim() || SHARED_STAGING_STACK_NAME;
  if (!STAGING_STACK_NAME_PATTERN.test(stackName)) {
    throw new StagingStackError(
      `Invalid ${inputName} "${stackName}". Must be kebab-case, 2-13 chars, [a-z0-9-].`,
    );
  }
  if (stackName === "default" || stackName === "production") {
    throw new StagingStackError(
      `"${stackName}" is reserved. Use blank for shared staging or choose a named staging alias.`,
    );
  }
  return stackName;
}

export function isSharedStagingStack(stackName: string): boolean {
  return stackName === SHARED_STAGING_STACK_NAME;
}

export function getAgentCoordinatorWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "cloudflare-agent-coordinator-staging"
    : `cloudflare-agent-coordinator-${stackName}`;
}

export function getAgentCoordinatorBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return `https://${getAgentCoordinatorWorkerName({ stackName })}.replo.workers.dev`;
}

export function getAirlockWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "airlock-staging"
    : `airlock-${stackName}`;
}

export function getAirlockBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? STAGING_AIRLOCK_BASE_URL
    : `https://${getAirlockWorkerName({ stackName })}.replo.workers.dev`;
}

export function getAirlockJwksUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return `${getAirlockBaseUrl({ stackName })}/.well-known/jwks.json`;
}

export function getBedrockApiWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "bedrock-api-staging"
    : `bedrock-api-${stackName}`;
}

export function getBedrockApiBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "https://bedrock.staging-api.replo.app"
    : `https://${getBedrockApiWorkerName({ stackName })}.replo.workers.dev`;
}

export function getPublisherApiBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return `https://${stackName}-api.aws.replointernal.com`;
}

export function getFilesWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "cloudflare-files-worker-staging"
    : `cloudflare-files-worker-${stackName}`;
}

export function getFilesBaseUrl({ stackName }: { stackName: string }): string {
  return isSharedStagingStack(stackName)
    ? "https://files-staging.replousercontent.com"
    : `https://${getFilesWorkerName({ stackName })}.replo.workers.dev`;
}

export function getGitProxyWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "git-proxy-staging"
    : `git-proxy-${stackName}`;
}

export function getGitProxyBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? SHARED_STAGING_GIT_PROXY_PUBLIC_ORIGIN
    : `https://${getGitProxyWorkerName({ stackName })}.replo.workers.dev`;
}

export function getSandboxProxyWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "sandbox-proxy-staging"
    : `sandbox-proxy-${stackName}`;
}

export function getAgentDoWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return isSharedStagingStack(stackName)
    ? "cloudflare-agent-do-staging"
    : `cloudflare-agent-do-${stackName}`;
}

export function getSandboxProxyBaseUrl({
  stackName,
}: {
  stackName: string;
}): string {
  return `https://${getSandboxProxyWorkerName({ stackName })}.replo.workers.dev`;
}

export function getRateLimiterWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return `rate-limiter-${stackName}`;
}

export function getEmailRendererWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return `email-renderer-${stackName}`;
}

export function getImageProcessorWorkerName({
  stackName,
}: {
  stackName: string;
}): string {
  return `image-processor-${stackName}`;
}

/**
 * Every Worker a named stack deploys, in the order a reap deletes them:
 * bedrock-api first and the coordinator last, so a reap that stops partway
 * leaves the coordinator marking the stack and can simply be re-run.
 */
export const NAMED_STACK_WORKER_GETTERS: readonly ((options: {
  stackName: string;
}) => string)[] = [
  getBedrockApiWorkerName,
  getEmailRendererWorkerName,
  getImageProcessorWorkerName,
  getRateLimiterWorkerName,
  getAirlockWorkerName,
  getGitProxyWorkerName,
  getSandboxProxyWorkerName,
  getFilesWorkerName,
  getAgentDoWorkerName,
  getAgentCoordinatorWorkerName,
];

/**
 * Names a reap refuses even though they pass the stack-name pattern: shared
 * staging, the names `getStagingStackName` reserves, and standalone workers.
 */
export const REAP_REFUSED_STACK_NAMES: ReadonlySet<string> = new Set([
  SHARED_STAGING_STACK_NAME,
  // Deploy's concurrency group for shared staging is deploy-staging-shared.
  "shared",
  "production",
  "default",
  "development",
  ...NON_STACK_WORKER_NAMES,
]);
// CI deploys its own `<service>-ci-<run>` workers (airlock-ci-21115 is one).
const REAP_REFUSED_STACK_PREFIX = "ci-";

class StackNotReapableError extends Error {}

export function assertReapableStackName(stackName: string) {
  if (!STAGING_STACK_NAME_PATTERN.test(stackName)) {
    throw new StackNotReapableError(
      `Invalid stack name "${stackName}". Must be kebab-case, 2-13 chars, [a-z0-9-].`,
    );
  }
  if (
    REAP_REFUSED_STACK_NAMES.has(stackName) ||
    stackName.startsWith(REAP_REFUSED_STACK_PREFIX)
  ) {
    throw new StackNotReapableError(
      `"${stackName}" is not a reapable stack (reserved/shared name). Nothing was called.`,
    );
  }
}

export type ImageTarget =
  | { type: "stack"; stack: string }
  | { type: "snapshot"; snapshot: string };

// Inverse of `describeStackTarget` in
// apps/eval-supervisor/container/stack-target-contract.mts ("stack <n> @ <sha8>")
// and the supervisor's snapshot form ("snapshot <name>").
const STACK_IMAGE_TARGET_PATTERN = /^stack (\S+) @ [0-9a-f]{8}$/;
const SNAPSHOT_IMAGE_TARGET_PATTERN = /^snapshot (\S+)$/;

/** The supervisor's `imageTarget` status field; null when it has neither shape. */
export function parseImageTarget(imageTarget: string): ImageTarget | null {
  const stack = STACK_IMAGE_TARGET_PATTERN.exec(imageTarget)?.[1];
  if (stack !== undefined && STAGING_STACK_NAME_PATTERN.test(stack)) {
    return { type: "stack", stack };
  }
  const snapshot = SNAPSHOT_IMAGE_TARGET_PATTERN.exec(imageTarget)?.[1];
  return snapshot === undefined ? null : { type: "snapshot", snapshot };
}
